import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  CircularProgress,
  Typography
} from '@mui/material';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TimeAgo from '../../../../components/utils/TimeAgo';
import TopBar from '../../../../components/utils/TopBar';

import {
  GetNotificationsOverviewBIZnotifications
} from '../../../../redux/actions/business/notifications/NotificationsOverviewActions';

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

const Section = ({ title, hint, children }) => (
  <Box mb={"22px"}>
    <Typography variant='subtitle2' sx={{ mb: hint ? "2px" : "10px" }}>{title}</Typography>
    {
      hint
      &&
      <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mb: "10px" }}>
        {hint}
      </Typography>
    }
    {children}
  </Box>
);

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const overview = useSelector(state => state["business.notifications"]?.notificationsOverview);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleGetOverview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleGetOverview() {
    await dispatch(GetNotificationsOverviewBIZnotifications(setLoading));
  }

  const email = overview?.email;
  const templates = overview?.email_templates;
  const sms = overview?.sms;
  const otp = overview?.otp;

  const sentTotal = Number(email?.total || 0);
  const problem = Number(email?.problem || 0);
  const noResponse = Number(email?.no_response || 0);
  const last7d = Number(email?.last_7d || 0);

  // Only rows that are both actionable and non-zero.
  const attention = [
    {
      count: problem,
      one: "email was rejected by the mail server or accepted by nobody",
      many: "emails were rejected by the mail server or accepted by nobody",
      to: "/business/notifications/email-activities",
    },
    {
      count: noResponse,
      one: "email activity has no recorded mail-server response",
      many: "email activities have no recorded mail-server response",
      to: "/business/notifications/email-activities",
    },
    {
      count: Number(templates?.empty_body || 0),
      one: "email template has an empty body and would send blank",
      many: "email templates have an empty body and would send blank",
      to: "/business/notifications/email-templates",
    },
    {
      count: Number(otp?.expired_unused || 0),
      one: "OTP expired without ever being used",
      many: "OTPs expired without ever being used",
      to: "/business/notifications/otp-records",
    },
    {
      // the heartbeat: a mailer that has silently stopped looks exactly like this
      count: sentTotal > 0 && last7d === 0 ? 1 : 0,
      one: "no email has been sent in the last 7 days",
      many: "no email has been sent in the last 7 days",
      to: "/business/notifications/email-activities",
    },
  ]
    .filter((i) => Number(i.count) > 0)
    .map((i) => ({ ...i, label: Number(i.count) === 1 ? i.one : i.many }));

  const deliveryTiles = [
    { label: "Emails sent", value: sentTotal, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/notifications/email-activities") },
    { label: "Delivered", value: Number(email?.delivered || 0), accent: TILE_ACCENT.good, onClick: () => navigate("/business/notifications/email-activities") },
    { label: "Rejected / not accepted", value: problem, accent: problem ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/notifications/email-activities") },
    { label: "Last 24 hours", value: Number(email?.last_24h || 0), accent: TILE_ACCENT.info, onClick: () => navigate("/business/notifications/email-activities") },
    { label: "Last 7 days", value: last7d, accent: last7d === 0 && sentTotal > 0 ? TILE_ACCENT.warn : TILE_ACCENT.info, onClick: () => navigate("/business/notifications/email-activities") },
  ];

  const otpTiles = [
    { label: "OTPs issued", value: Number(otp?.total || 0), accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/notifications/otp-records") },
    { label: "Used", value: Number(otp?.used || 0), accent: TILE_ACCENT.good, onClick: () => navigate("/business/notifications/otp-records") },
    { label: "Live right now", value: Number(otp?.live || 0), accent: TILE_ACCENT.info, onClick: () => navigate("/business/notifications/otp-records") },
    { label: "Expired unused", value: Number(otp?.expired_unused || 0), accent: Number(otp?.expired_unused) ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/notifications/otp-records") },
  ];

  const templateTiles = [
    { label: "Email templates", value: Number(templates?.total || 0), accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/notifications/email-templates") },
    { label: "Marked important", value: Number(templates?.important || 0), accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/notifications/email-templates") },
    { label: "Empty body", value: Number(templates?.empty_body || 0), accent: Number(templates?.empty_body) ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/notifications/email-templates") },
    { label: "SMS templates", value: Number(sms?.templates || 0), accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/notifications/sms-templates") },
    { label: "SMS sent", value: Number(sms?.activities || 0), accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/notifications/sms-activities") },
  ];

  // Breakdowns only earn their place once the column actually varies.
  const tags = templates?.by_tag || [];
  const otpBreakdown = otp?.breakdown || [];

  return (
    <Box>
      <TopBar
        title={"Notifications"}
        button1Text={"Refresh"}
        button1OnClick={() => handleGetOverview()}
        />

      <Typography
        variant='caption'
        sx={{ color: LABEL_COLOR, display: "block", mb: "20px" }}>
        Delivery outcome is derived from the mail server&apos;s own response on each
        activity, not from a status column. Read-only.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        !overview
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <Typography>Overview unavailable</Typography>
        </Box>
        :
        <Box>
          <Section title={"Needs attention"}>
            {
              attention.length === 0
              ?
              <Box className="flexCenter_Row" gap={"10px"}
                sx={{ border: `1px solid ${TILE_ACCENT.good}40`, background: `${TILE_ACCENT.good}14`,
                      borderRadius: "12px", padding: "14px 16px" }}>
                <CheckCircleOutlineOutlinedIcon sx={{ color: TILE_ACCENT.good }} />
                <Typography variant='body2'>
                  Nothing to action — every email was accepted, every template has a
                  body, and no OTP expired unused.
                </Typography>
              </Box>
              :
              <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
                {
                  attention.map((item, index) => (
                    <Box
                      key={index}
                      className="flexCenterSBRow"
                      onClick={() => navigate(item.to)}
                      sx={{
                        border: `1px solid ${TILE_ACCENT.warn}40`,
                        background: `${TILE_ACCENT.warn}14`,
                        borderRadius: "12px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        transition: "all .15s",
                        "&:hover": { background: `${TILE_ACCENT.warn}24`, borderColor: `${TILE_ACCENT.warn}80` },
                      }}>
                      <Box className="flexCenter_Row" gap={"12px"}>
                        <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.warn }} />
                        <Typography variant='body2'>
                          <b>{item.count}</b>&nbsp;{item.label}
                        </Typography>
                      </Box>
                      <ChevronRightIcon sx={{ color: LABEL_COLOR }} />
                    </Box>
                  ))
                }
              </Box>
            }
          </Section>

          <Section
            title={"Email delivery"}
            hint={"An email counts as delivered when the mail server accepted it and rejected nobody."}>
            <SummaryTiles tiles={deliveryTiles} />
            {
              email?.latest_at
              &&
              <Box className='flexCenter_Row' gap={"8px"}>
                <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                  Most recent send
                </Typography>
                <TimeAgo value={email.latest_at} />
              </Box>
            }
          </Section>

          <Section
            title={"One-time passcodes"}
            hint={"A high expired-unused count usually means the message is not reaching people."}>
            <SummaryTiles tiles={otpTiles} />
            {
              otpBreakdown.length > 0
              &&
              <Box className='flexCenter_Row' gap={"8px"} flexWrap={"wrap"}>
                {
                  otpBreakdown.map((row, i) => (
                    <Chip key={i} size='small' variant='outlined' color='primary'
                      label={`${row.channel} · ${row.source} · ${row.count}`} />
                  ))
                }
              </Box>
            }
          </Section>

          <Section title={"Templates"}>
            <SummaryTiles tiles={templateTiles} />
            {
              tags.length > 0
              &&
              <Box className='flexCenter_Row' gap={"8px"} flexWrap={"wrap"}>
                {
                  tags.map((row, i) => (
                    <Chip key={i} size='small' variant='outlined' color='primary'
                      label={`${row.tag} · ${row.count}`} />
                  ))
                }
              </Box>
            }
          </Section>
        </Box>
      }
    </Box>
  )
}

export default HomePage;
